import { IntegrationConfigSyncStatus } from './PerformanceScoreChart'

export interface Integration {
  uuid: string
  productUuid: string
  productName: string
  vendorUuid: string
  createdAt: string | null
  updatedAt: string | null
  integrationUuid: string
  organizationUuid: string | null
  enabled: boolean
  credentialKey: string | null
  slug: string
  logo: string
  websiteUrl: string
  description: string
  category: string
  errorCode?: string | null
  lastUpdatedAt?: string | null
  statusCode?: string | null
}

export interface ManifestIntegrationConfigSchema {
  name: string
  type: string
  title: string
  airbyte_secret?: boolean
  required: boolean
  description?: string
  order?: number
  default?: string
  enum?: string[]
}

export interface ManifestIntegrationConfigAssetAttributes {
  name: string
  type: string
  required: boolean
}

export interface ManifestIntegrationConfigAssets {
  title: string
  type: string
  mime: string
  path: string
  required: boolean
  attributes: ManifestIntegrationConfigAssetAttributes[]
}

export interface ManifestIntegrationConfig {
  schema: ManifestIntegrationConfigSchema[]
  assets?: ManifestIntegrationConfigAssets[]
}

export interface ManifestIntegrationSupportedCPI {
  name: string
  title: string
  description: string
  display_name?: string
}

export interface ManifestIntegrationSupportedDataStreamStateSchema {
  name: string
  type: string
  required: boolean
}

export interface ManifestIntegrationSupportedDataStream {
  name: string
  stream_state_schema: ManifestIntegrationSupportedDataStreamStateSchema[]
}

export interface ManifestIntegrationVendor {
  name: string
  logo_path: string
  display_name: string
}

/* Supported Integrations */
export interface ManifestIntegration {
  name: string
  version: string
  description: string
  categories: string[]
  instances: number
  product: string
  vendor: ManifestIntegrationVendor
  manifest_version: string
  documentation: {
    path: string
  }
  is_supports_aca: boolean
  logo_path: string
  config: ManifestIntegrationConfig
  supported_cpis: ManifestIntegrationSupportedCPI[]
  supported_data_streams: ManifestIntegrationSupportedDataStream[]
  published_at: string
  display_name: string
  is_v2: boolean
}

export interface DataModelSettingsURIAttribute {
  description: string
  name: string
  source_name: string
  source_type: 'record' | 'config'
  type: string
  value?: string
}

export interface InstanceConfigDataModelSettings {
  name: string
  uri_template: string
  uri_attributes: DataModelSettingsURIAttribute[]
}

export interface ConfiguredInstanceConfig {
  skip_el: boolean
  data_model_settings?: InstanceConfigDataModelSettings[]
}

export interface IntegrationInstance {
  id: string
  name: string
  tenant_id: string
  description?: string
  config_name?: string
  integration_name: string
  prefect_deployment_id: string
  created_at: string
  created_by: string
  updated_at: string
  updated_by: string
  categories: string[]
  connection_id: string
  source_id: string
  instance_name?: string
  deleted?: boolean
  jiraProjects?: string[]
  syncStatus: IntegrationConfigSyncStatus
  is_v2: boolean
  config?: ConfiguredInstanceConfig
  /* Authoritative logo filename joined from the supported-integrations manifest (manifest.logo_path).
   * Not returned by the configured-instances endpoint — populated client-side so logos render the same
   * everywhere the dropdown does. See getInstanceCircleLogo in lib/utils. */
  logo_path?: string
}

/* Configured Integrations */
export interface UserConfiguredIntegration {
  created_at: string
  id: string
  integration_name: string
  tenant_id: string
}

export interface ConfiguredDataSource {
  id: string
  name: string
  tenant_id: string
  integration_name: string
  connection_id: string
  prefect_deployment_id: string
  source_id: string
  description: string
  created_at: string
  created_by: string
  updated_at: string
  updated_by: string
}

export interface IntegrationsFilters {
  category: string[]
  vendor: string[]
  configurationStatus: string[]
  product: string[]
}

export enum IntegrationsSort {
  AZ = 'A to Z',
  ZA = 'Z to A',
  LatestCreated = 'Latest Created',
  OldestCreated = 'Oldest Created',
  NewestFirst = 'Newest to Oldest',
  OldestFirst = 'Oldest to Newest',
  ConfiguredFirst = 'Configured First',
  NonconfiguredFirst = 'Non-configured First'
}

export interface SSMLocation {
  nist: string
  asset: string
}
